/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, Link, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { RootState, useAppDispatch } from "@store/store";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { useLazyGetBanksByCodeQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import {
  useDeleteBankAccountMutation,
  useLazyGetBankAccountsQuery,
  usePostBankAccountsMutation,
  usePutApplicationCompletedMutation,
  usePutBankAccountMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery, usePatchDocumentMutation } from "@api/documents";
import { CustomDialog } from "@components/CustomDialog";
import { InactivityHandler } from "@components/InactivityHandler";
import { UUID } from "crypto";
import { BankAccount, DocumentResponse } from "@interfaces/response";
import { PayloadBankAccount } from "@interfaces/request";
import { ApiError } from "@interfaces/response";
import { DOCUMENTS_CATALOG, INVALID_BANK_ACCOUNT_CODE, DEFAULT_NUMBER_BANK_STATEMENTS } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { BankAccountProps } from "@interfaces/catalogs";
import * as Sentry from "@sentry/react";
import { buildProductRoute, getBankInformationConfig, getBankStatementIndex } from "@helpers/funciones";

const MAX_ACCOUNTS_ALLOWED = 5;

const createEmptyPfBankAccount = (numberOfStatements: number): BankAccountProps => ({
  id: "",
  clabe: "",
  bank_id: "",
  bank_name: "",
  bank_statements: Array(numberOfStatements).fill(undefined),
});

const InformacionBancariaPf = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const companyame = useSelector((state: RootState) => state.brand.name);

  const [listDocuments, setListDocuments] = useState<DocumentResponse[]>([]);
  const [acountToDelete, setAcountToDelete] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [totalStatements, setTotalStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);
  const [requiredStatements, setRequiredStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);

  const handleOnClickRestart = () => {
    navigate(buildProductRoute(location.pathname, "expediente_pf"));
  };

  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPutBankAccount] = usePutBankAccountMutation();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();

  const formik = useFormik({
    initialValues: {
      accounts: [createEmptyPfBankAccount(DEFAULT_NUMBER_BANK_STATEMENTS)],
    },
    validationSchema: yup.object().shape({
      accounts: yup.array().of(
        yup.object().shape({
          clabe: yup
            .string()
            .min(18, "Formato incorrecto, debe tener 18 dígitos")
            .required("Campo requerido.")
            .test("unique", "La cuenta bancaria ya se encuentra registrada.", function (value) {
              const num_results: number = formik.values.accounts.filter((item: any) => item.clabe === value).length;
              return num_results > 1 ? false : true;
            }),
          bank_id: yup.string().required("Clabe bancaria incorrecta, validar nuevamente."),
          bank_statements: yup.array().of(
            yup
              .string()
              .nullable()
              .test("required-if-min", "Campo requerido.", function (value, params) {
                return getBankStatementIndex(params.path) < requiredStatements ? Boolean(value) : true;
              }),
          ),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        for (let index = 0; index < values.accounts.length; index++) {
          const account = values.accounts[index];
          const payload: PayloadBankAccount = {
            loan_application_id: applicationData.id,
            client_details_id: personalData.id,
            person_id: null,
            account: 0,
            account_holder: "",
            clabe: account.clabe,
            bank_id: account.bank_id as UUID,
            documents: account.bank_statements
              .filter((item) => item !== undefined)
              .map((item, index) => ({
                document_id: item as string,
                document_order: index + 1,
              })),
          };

          if (account.id) {
            await triggerPutBankAccount({
              solicitudId: applicationData.id,
              bankAccountId: account.id,
              body: payload,
            }).unwrap();
          } else {
            await triggerPostBankAccount({
              solicitudId: applicationData.id,
              body: payload,
            }).unwrap();
          }
        }

        await putAapplicationCompleted({
          id_sol: applicationData.id,
          body: {
            bank_information_status: "COMPLETO",
          },
        }).unwrap();

        handleOnClickRestart();
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaPfae-submit",
          },
        });
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handleOnAddAccountBank = () => {
    if (formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED) return;
    formik.setFieldValue("accounts", [...formik.values.accounts, createEmptyPfBankAccount(totalStatements)]);
  };

  const getBankNameByCode = useCallback(
    async (bank_code: string) => {
      try {
        const bank_info = await triggerGetBanks(bank_code).unwrap();
        if (bank_info) {
          return {
            id: bank_info.id,
            name: bank_info.name,
          };
        } else {
          return null;
        }
      } catch (error) {
        console.error("Error al obtener el banco:", error);
        return null;
      }
    },
    [listDocuments],
  );

  const handleChangeCLABE = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = event.target;
    const only_numbers = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(name, only_numbers);

    const bank_code = only_numbers.substring(0, 3);
    const bank_info = bank_code.length === 3 ? await getBankNameByCode(bank_code) : null;
    if (bank_info) {
      formik.setFieldValue(`accounts[${index}].bank_id`, bank_info?.id || "");
      formik.setFieldValue(`accounts[${index}].bank_name`, bank_info?.name || "");
    } else {
      formik.setFieldValue(`accounts[${index}].bank_id`, "");
      formik.setFieldValue(`accounts[${index}].bank_name`, "");
    }
  };

  const handleOnOpenModal = (index: number) => {
    setAcountToDelete(index);
  };

  const handleOnCloseModal = () => {
    setAcountToDelete(undefined);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      if (acountToDelete === undefined) return;

      const account_to_delete = formik.values.accounts[acountToDelete];

      if (!account_to_delete) {
        setAcountToDelete(undefined);
        return;
      }

      const delete_promises: Promise<any>[] = [];

      // desactivar documentos
      for (let index = 0; index < account_to_delete.bank_statements.length; index++) {
        const bank_statement = account_to_delete.bank_statements[index];
        if (bank_statement) {
          delete_promises.push(
            triggerPatchDocument({
              documentId: bank_statement as UUID,
              body: {
                status: false,
              },
            }),
          );
        }
      }

      await Promise.all(delete_promises);

      // eliminar cuenta bancaria
      if (account_to_delete.id) {
        await deleteBankAccount({
          solicitudId: applicationData.id,
          accountId: account_to_delete.id,
        }).unwrap();
      }

      const new_accounts = formik.values.accounts.filter((_, index) => index !== acountToDelete);
      formik.setFieldValue("accounts", new_accounts);
      setAcountToDelete(undefined);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaPfae-handleDeleteAccount",
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const [tax_systems, list_bank_accounts, documents] = await Promise.all([
          getTaxSystems(applicationData.type).unwrap(),
          getBankAccounts(applicationData.id).unwrap(),
          getDocumentsByAppId({
            loanApplicationId: applicationData.id,
            clientDetailsId: personalData.id,
            status: true,
          }).unwrap(),
        ]);

        const { total_items, required_items } = getBankInformationConfig(
          tax_systems?.config_extra?.bank_information_config?.company,
        );

        setTotalStatements(total_items);
        setRequiredStatements(required_items);

        const bank_accounts: BankAccount[] = list_bank_accounts.filter(
          (item) => item.client_details_id === personalData.id,
        );

        const documentsIds = documents.map((item) => item.id);
        const accounts: BankAccountProps[] = [];
        const num_statements_required = total_items;

        for (let index = 0; index < bank_accounts.length; index++) {
          const firstThree = (bank_accounts[index].clabe || "").toString().substring(0, 3);

          if (firstThree === INVALID_BANK_ACCOUNT_CODE) {
            const cuenta_clabe = (bank_accounts[index].clabe || "").toString().padStart(18, "0");

            const account_documents = bank_accounts[index].documents || [];
            const account_documents_available = account_documents.filter((item) =>
              documentsIds.includes(item.document_id),
            );

            const invalid_acc = {
              id: bank_accounts[index].id,
              clabe: cuenta_clabe,
              bank_id: "",
              bank_name: "",
              bank_statements: Array(num_statements_required).fill(undefined),
            };

            for (let i = 0; i < num_statements_required; i++) {
              if (account_documents_available[i]) {
                invalid_acc.bank_statements[i] = account_documents_available[i].document_id;
              }
            }

            const pushIndex = accounts.length;
            accounts.push(invalid_acc);

            // Limpiar input CLABE y mostrar un error en el formulario para que el usuario ingrese una válida
            try {
              formik.setFieldValue(`accounts[${pushIndex}].clabe`, "");
              formik.setFieldValue(`accounts[${pushIndex}].bank_name`, "");
              formik.setFieldTouched(`accounts[${pushIndex}].clabe`, true);
              formik.setFieldError(`accounts[${pushIndex}].clabe`, "Agrega una CLABE bancaria válida.");
            } catch (e) {
              console.warn("No se pudo marcar/limpiar campo en Formik:", e);
            }

            continue;
          }

          const cuenta_clabe = (bank_accounts[index].clabe || "").toString().padStart(18, "0");
          const bank_code = cuenta_clabe.toString().substring(0, 3);
          const bank_info = await getBankNameByCode(bank_code);

          const account_documents = bank_accounts[index].documents || [];
          const account_documents_available = account_documents.filter((item) =>
            documentsIds.includes(item.document_id),
          );

          const new_accout = {
            id: bank_accounts[index].id,
            clabe: cuenta_clabe,
            bank_id: bank_info?.id || "",
            bank_name: bank_info?.name || "",
            bank_statements: Array(num_statements_required).fill(undefined),
          };

          for (let index = 0; index < num_statements_required; index++) {
            if (account_documents_available[index]) {
              new_accout.bank_statements[index] = account_documents_available[index].document_id;
            }
          }

          accounts.push(new_accout);
        }

        setListDocuments(documents);

        formik.setFieldValue("accounts", accounts.length ? accounts : [createEmptyPfBankAccount(total_items)]);
      } catch (error) {
        console.log("Error al obtener la información bancaria:", error);
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaPfae-initial_fetch",
          },
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const customText = (companyName: string): React.ReactNode => {
    if (companyName.toUpperCase() === "CREDIVIVE") {
      return (
        <>
          <Typography
            variant={isMobileOrTablet ? "body3" : "body2"}
            fontWeight={600}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            "La cuenta bancaria que nos proporcionará será utilizada para dispersión de crédito y domiciliación"
          </Typography>
        </>
      );
    }

    if (companyName.toUpperCase() === "VIVEMOTO") {
      return (
        <>
          <Typography
            variant={isMobileOrTablet ? "body3" : "body2"}
            fontWeight={600}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            "Cuenta bancaria requerida para realizar cargos por domiciliación mensuales de las rentas."
          </Typography>
        </>
      );
    }

    return (
      <>
        
      </>
    );
  };

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <InactivityHandler />
          <Box maxWidth="sm" mx="auto" mt={4} px={2}>
            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={700}
              color={theme?.palette?.primary?.dark}
            >
              Información bancaria
            </Typography>

            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              mt={2}
            >
              <strong>1</strong> de 1
            </Typography>

            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.main}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              mt={2}
            >
              Ingresa los datos de la cuenta en dónde concentras la mayor facturación de tu negocio, a esta cuenta
              depositaremos tu crédito
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              {formik.values.accounts.map((account: BankAccountProps, index: number) => (
                <Box key={index} mt={index > 0 ? 4 : 0}>
                  <TextField
                    name={`accounts[${index}].clabe`}
                    label="Cuenta CLABE"
                    margin="normal"
                    type="text"
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeCLABE(event, index)}
                    value={formik.values.accounts[index].clabe}
                    error={
                      formik.touched.accounts?.[index]?.clabe &&
                      Boolean((formik.errors.accounts as any)?.[index]?.clabe)
                    }
                    helperText={
                      formik.touched.accounts?.[index]?.clabe
                        ? (formik.errors.accounts as any)?.[index]?.clabe ||
                          (formik.errors.accounts as any)?.[index]?.bank_id
                        : ""
                    }
                    inputProps={{ maxLength: 18 }}
                    disabled={isLoading || formik.isSubmitting}
                  />

                  <TextField
                    name={`accounts[${index}].bank_name`}
                    label="Banco"
                    margin="normal"
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.accounts[index].bank_name}
                    error={
                      formik.touched.accounts?.[index]?.bank_name &&
                      Boolean((formik.errors.accounts as any)?.[index]?.bank_name)
                    }
                    helperText={
                      formik.touched.accounts?.[index]?.bank_name
                        ? (formik.errors.accounts as any)?.[index]?.bank_name
                        : ""
                    }
                    disabled={true}
                  />

                  <Box my={2}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={theme?.palette?.fontWeights?.regular}
                      color={theme?.palette?.primary?.dark}
                    >
                      Debes cargar los estado de cuenta bancario de los últimos {totalStatements} meses de una o mas
                      cuentas para cubrir la capacidad de pago necesaria para el crédito solicitado. Completa al menos{" "}
                      {requiredStatements}.
                    </Typography>
                  </Box>

                  {account.bank_statements.map((statement, statementIndex) => (
                    <CustomUploadFileV2
                      key={`bank_statement_${index}_${statementIndex}`}
                      mt={2}
                      inputName={`bank_statement_${index}_${statementIndex}`}
                      uploadedFile={listDocuments.find((item) => item.id === statement) || undefined}
                      setUploadedFile={(newUpload) => {
                        if (newUpload) {
                          setListDocuments((prev) => [...prev, newUpload]);
                        } else {
                          setListDocuments((prev) => prev.filter((item) => item.id !== statement));
                        }
                        formik.setFieldValue(
                          `accounts[${index}].bank_statements[${statementIndex}]`,
                          newUpload?.id || null,
                        );
                      }}
                      optionalName={`Estado de cuenta ${statementIndex + 1}`}
                      optionalDescription=""
                      metadata={{
                        fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                        uploadType: "client",
                        clientDetailsId: personalData.id,
                      }}
                      disabled={isLoading || formik.isSubmitting}
                      required={statementIndex < requiredStatements}
                    />
                  ))}

                  {index > 0 && (
                    <Box textAlign={"left"} my={2} mt={2}>
                      <Link onClick={() => handleOnOpenModal(index)} sx={{ cursor: "pointer" }}>
                        <Typography variant="body1" fontWeight={400}>
                          - Eliminar cuenta bancaria
                        </Typography>
                      </Link>
                    </Box>
                  )}
                </Box>
              ))}

              <Box textAlign={"left"} my={2}>
                <Link
                  onClick={handleOnAddAccountBank}
                  sx={{
                    cursor: formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED ? "not-allowed" : "pointer",
                    opacity: formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED ? 0.5 : 1,
                  }}
                >
                  <Typography variant="body1" fontWeight={400}>
                    + Agregar cuenta bancaria
                  </Typography>
                </Link>
              </Box>

              <Box my={3} px={3} alignContent={"center"} textAlign={"center"}>
                {customText(companyame)}
              </Box>

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0px", height: "100%" }}
                    onClick={handleOnClickRestart}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="default"
                    disabled={isLoading || !formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={acountToDelete !== undefined}
        onClose={handleOnCloseModal}
        itemTitle={<></>}
        itemContent={
          <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.dark}
              fontWeight={600}
              variant={isMobileOrTablet ? "h5" : "h6"}
            >
              ¿Estás seguro de eliminar la cuenta bancaria?
            </Typography>
            <Box display={"flex"} columnGap={2} justifyContent={"center"} mt={3}>
              <Button
                variant="blue_outlined"
                sx={{ py: 0, px: 1, width: "110px" }}
                onClick={handleOnCloseModal}
                disabled={isDeleting}
              >
                Regresar
              </Button>

              <Button
                variant="default"
                sx={{ width: "110px" }}
                onClick={() => handleDeleteAccount()}
                disabled={isDeleting}
              >
                Eliminar
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};

export default InformacionBancariaPf;
